/**
 * Telecommunications Site Safety Audit — section + item template.
 * Mirrors the Force2 docx form so every audit has the same checklist
 * (and the compliance % is comparable across audits).
 */
export const AUDIT_SECTIONS = [
  {
    key: "pre_job_planning", title: "1. Pre-Job Planning",
    items: [
      ["jha", "Job Hazard Analysis (JHA) completed and reviewed"],
      ["scope_reviewed", "Scope of work reviewed with crew"],
      ["emergency_contacts", "Emergency contact information available"],
      ["site_access", "Site access requirements understood"],
      ["customer_reqs", "Customer requirements reviewed"],
      ["tailgate", "Daily tailgate safety meeting conducted"],
    ],
  },
  {
    key: "ppe", title: "2. Personal Protective Equipment (PPE)",
    items: [
      ["hard_hat", "Hard hats worn and in good condition"],
      ["safety_glasses", "Safety glasses worn"],
      ["hi_vis", "High visibility vest/shirt worn"],
      ["gloves", "Work gloves available and used"],
      ["footwear", "Safety footwear worn"],
      ["fall_protection", "Fall protection inspected and serviceable"],
      ["ppe_condition", "PPE free of damage and defects"],
    ],
  },
  {
    key: "fall_protection", title: "3. Fall Protection / Climbing Safety",
    items: [
      ["tie_off_100", "100% tie-off maintained"],
      ["harness", "Full body harness inspected"],
      ["lanyards", "Lanyards and connectors inspected"],
      ["rescue_plan", "Rescue plan available on-site"],
      ["rescue_equip", "Rescue equipment available and inspected"],
      ["anchor_points", "Anchor points approved and utilized"],
      ["climbers_certified", "Tower climbers certified"],
    ],
  },
  {
    key: "rf_safety", title: "4. RF Safety",
    items: [
      ["rf_training", "RF awareness training current"],
      ["rf_monitor", "RF monitor present and operational"],
      ["rf_signs", "RF hazard signs posted"],
      ["rf_permits", "RF work permits obtained (if required)"],
      ["rf_areas", "RF controlled areas identified"],
      ["carrier_shutdowns", "Required carrier shutdowns verified"],
    ],
  },
  {
    key: "equipment_tools", title: "5. Equipment & Tools",
    items: [
      ["tools_safe", "Tools in safe working condition"],
      ["ext_cords", "Extension cords inspected"],
      ["ladders", "Ladders inspected and properly used"],
      ["hoisting", "Hoisting equipment inspected"],
      ["test_equip", "Test equipment calibrated (if required)"],
      ["damaged_removed", "Damaged equipment removed from service"],
    ],
  },
  {
    key: "electrical", title: "6. Electrical Safety",
    items: [
      ["loto", "Lockout/Tagout procedures followed"],
      ["panels_secured", "Electrical panels secured"],
      ["energized_protected", "Exposed energized parts protected"],
      ["qualified_personnel", "Qualified personnel performing electrical work"],
      ["arc_flash", "Arc flash requirements followed"],
      ["grounding", "Grounding and bonding requirements met"],
    ],
  },
  {
    key: "housekeeping", title: "7. Housekeeping",
    items: [
      ["organized", "Work area organized"],
      ["trip_hazards", "Trip hazards eliminated"],
      ["materials_stored", "Materials properly stored"],
      ["waste_removed", "Waste removed regularly"],
      ["site_clean", "Site left clean and secure"],
    ],
  },
  {
    key: "vehicle", title: "8. Vehicle Safety",
    items: [
      ["vehicle_inspection", "Vehicle inspection completed"],
      ["fire_extinguisher", "Fire extinguisher available"],
      ["first_aid", "First aid kit stocked"],
      ["vehicle_docs", "Proper vehicle documentation available"],
      ["load_secured", "Load securement compliant"],
    ],
  },
  {
    key: "environmental", title: "9. Environmental & Weather Conditions",
    items: [
      ["weather_eval", "Weather conditions evaluated"],
      ["heat_stress", "Heat stress mitigation implemented"],
      ["hydration", "Hydration available"],
      ["lightning", "Lightning monitoring conducted"],
      ["env_hazards", "Environmental hazards addressed"],
    ],
  },
];

export const WORK_TYPES = [
  "Microwave", "Tower", "Rooftop", "DAS", "Fiber",
  "Electrical", "Maintenance", "Construction",
];

/** Build an empty SiteAuditForm payload pre-populated with the
 *  section template. Used both on first-time render and on Reset. */
export function emptyAudit() {
  return {
    site_id: "", audit_date: "", audit_time: "",
    auditor_name: "", crew_lead: "", technicians_on_site: "",
    work_types: [], work_type_other: "",
    sections: AUDIT_SECTIONS.map((s) => ({
      key: s.key, title: s.title, comments: "",
      items: s.items.map(([key, label]) => ({ key, label, rating: "" })),
    })),
    positive_observations: "", deficiencies: "",
    corrective_actions: [],
    stop_work_issued: false, stop_work_reason: "",
    signatures: [],
  };
}
